/**
 * Security Utilities
 * Functions for handling sensitive data display and security
 */

/**
 * Mask account numbers - show only last 4 digits
 * @param {string} accountNumber - Full account number
 * @returns {string} Masked account number
 */
export const maskAccountNumber = (accountNumber) => {
  if (!accountNumber) return '';
  
  // If already masked, return as is
  if (accountNumber.includes('*') || accountNumber.includes('•')) {
    return accountNumber;
  }
  
  // For account numbers longer than 4 digits, mask all but last 4
  if (accountNumber.length > 4) {
    const lastFour = accountNumber.slice(-4);
    return `••••${lastFour}`;
  }
  
  // For shorter numbers, show as is (probably already last 4)
  return accountNumber;
};

/**
 * Mask email addresses - show only first letter and domain
 * @param {string} email - Full email address
 * @returns {string} Masked email
 */
export const maskEmail = (email) => {
  if (!email) return '';
  
  const [username, domain] = email.split('@');
  if (!username || !domain) return email;
  
  const maskedUsername = username.charAt(0) + '*'.repeat(Math.max(0, username.length - 1));
  return `${maskedUsername}@${domain}`;
};

/**
 * Mask phone numbers - show only last 4 digits
 * @param {string} phone - Full phone number
 * @returns {string} Masked phone number
 */
export const maskPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  
  if (digits.length >= 4) {
    const lastFour = digits.slice(-4);
    return `***-***-${lastFour}`;
  }
  
  return phone;
};

/**
 * Mask financial amounts for privacy (optional - for very sensitive displays)
 * @param {number} amount - Financial amount
 * @param {boolean} showFull - Whether to show full amount
 * @returns {string} Masked or full amount
 */
export const maskAmount = (amount, showFull = true) => {
  if (showFull) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
  
  return '••••••';
};

/**
 * Check if user has permission to view sensitive data
 * @param {string} dataType - Type of data (account, balance, etc.)
 * @param {object} permissions - User permissions object
 * @returns {boolean} Whether user can view the data
 */
export const canViewSensitiveData = (dataType, permissions = {}) => {
  // For now, return true - will be enhanced when we implement proper permissions
  return permissions[dataType] !== false;
};

/**
 * Sanitize data for logging (remove sensitive information)
 * @param {object} data - Data object to sanitize
 * @returns {object} Sanitized data object
 */
export const sanitizeForLogging = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const sensitiveFields = [
    'password', 'token', 'secret', 'key', 'accountNumber', 
    'routingNumber', 'ssn', 'socialSecurityNumber', 'accessToken'
  ];
  
  const sanitized = { ...data };
  
  const sanitizeValue = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeValue(item));
    }
    
    if (obj && typeof obj === 'object') {
      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        const lowerKey = key.toLowerCase();
        if (sensitiveFields.some(field => lowerKey.includes(field))) {
          result[key] = value ? '***HIDDEN***' : value;
        } else {
          result[key] = sanitizeValue(value);
        }
      }
      return result;
    }
    
    return obj;
  };
  
  return sanitizeValue(sanitized);
};

/**
 * Security headers for API requests
 */
export const getSecurityHeaders = () => {
  return {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block'
  };
}; 